import { useRef, type ComponentProps, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { Icon } from './icons';
import { t } from './i18n';
import { usePageTitle } from './shell/page-title';
import { buttonBase, secondaryLook, surfaceClass } from './styles';

/** Small building blocks shared by the console screens (brand: antoja-marca skill). */

export function Wordmark() {
  return (
    <span className="flex items-baseline gap-0.5 text-[22px] font-semibold tracking-[-0.04em]">
      {t('app.wordmark')}
      <span aria-hidden="true" className="inline-block size-1.5 rounded-full bg-logo-dot" />
    </span>
  );
}

/** White card: 20 px padding, 16 px between its title and its content. */
export function Card({
  title,
  children,
  className = '',
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`grid content-start gap-4 p-5 ${surfaceClass} ${className}`}>
      <h2 className="text-lg font-semibold tracking-[-0.01em]">{title}</h2>
      {children}
    </section>
  );
}

/** A group of blocks under a small muted heading ("Zonas piloto"). */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid gap-3">
      <h2 className="text-[15px] font-semibold text-muted">{title}</h2>
      {children}
    </section>
  );
}

/** Label, 8 px, the control, 6 px, then an optional hint. */
export function Field({
  label,
  htmlFor,
  hint,
  hintId,
  className = '',
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: ReactNode;
  /** Put it in the control's aria-describedby. */
  hintId?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`grid min-w-0 content-start ${className}`}>
      <label htmlFor={htmlFor} className="mb-2 text-sm font-medium">
        {label}
      </label>
      {children}
      {hint ? (
        <div id={hintId} className="mt-1.5 text-[13px] text-muted">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Bottom row of a form: what happened on the left, the actions on the right. `live` is
 * the outcome screen readers announce: always mounted, visible only when it says
 * something. With nothing visible the status column takes no room. Under 640 px the
 * actions take the full width, the primary one (last) on top.
 */
export function FormFooter({
  status,
  live = '',
  children,
}: {
  status?: ReactNode;
  live?: string;
  children?: ReactNode;
}) {
  return (
    <div className="relative flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-line pt-5">
      <div className="absolute min-w-0 flex-auto text-sm has-[>:not(.sr-only)]:static">
        {status}
        <p role="status" className={live ? '' : 'sr-only'}>
          {live}
        </p>
      </div>
      {children ? (
        <div className="ml-auto flex flex-wrap gap-2 max-[639px]:w-full max-[639px]:flex-col-reverse max-[639px]:*:w-full">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** An error or warning: an info icon and the words, on a quiet panel (no red: brand rule). */
export function Notice({
  id,
  children,
  ref,
}: {
  id?: string;
  children: ReactNode;
  /** To move focus to the message (it becomes focusable from script only). */
  ref?: Ref<HTMLParagraphElement>;
}) {
  return (
    <p
      id={id}
      ref={ref}
      tabIndex={ref ? -1 : undefined}
      role="alert"
      className="flex items-start gap-2 rounded-xl bg-canvas px-3.5 py-3 text-sm font-medium"
    >
      <span className="mt-px">
        <Icon name="info" />
      </span>
      <span>{children}</span>
    </p>
  );
}

type ButtonProps = ComponentProps<'button'> & {
  readonly variant?: 'primary' | 'secondary';
};

/**
 * One primary button per screen; everything else is secondary. Auto width, except the
 * main action of the narrow sign-in card.
 */
export function Button({ variant = 'secondary', className = '', ...props }: ButtonProps) {
  const look =
    variant === 'primary'
      ? 'border-0 bg-primary px-[18px] font-semibold text-on-primary hover:brightness-95'
      : secondaryLook;
  return <button type="button" className={`${buttonBase} ${look} ${className}`} {...props} />;
}

/** Page title: also the browser tab title and where focus lands after navigating. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string | undefined;
  actions?: ReactNode;
}) {
  const ref = usePageTitle(title);
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="grid min-w-0 gap-1.5">
        <h1
          ref={ref}
          tabIndex={-1}
          className="text-[30px] leading-tight font-semibold tracking-[-0.03em] outline-none"
        >
          {title}
        </h1>
        {subtitle ? <p className="text-[15px] text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Sign-in and other pages outside the shell: a white card on the grey canvas. */
export function CenteredPage({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas px-4 py-10 text-ink">
      <div className="grid w-full max-w-md gap-6 [grid-template-columns:minmax(0,1fr)]">
        <div className="px-1">
          <Wordmark />
        </div>
        <div className={`grid gap-6 p-8 max-[479px]:px-5 max-[479px]:py-6 ${surfaceClass}`}>
          {children}
        </div>
      </div>
    </main>
  );
}

/**
 * Segmented control (ConsolaLugares): a radio group with one tab stop; the arrow keys,
 * Home and End move the choice, like native radio buttons. Options are 44 px tall. On
 * phones they sit in two even columns (a long label may wrap) instead of scrolling out
 * of sight; with an odd number of options the first one takes the whole first row.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  optionLabel,
  count,
  countUnit,
}: {
  label: string;
  options: readonly T[];
  value: T;
  onChange: (next: T) => void;
  optionLabel: (option: T) => string;
  /** A number shown after the label (rows behind each option). */
  count?: (option: T) => number;
  /** Read after the number by screen readers ("lugares"). */
  countUnit?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(event: KeyboardEvent, index: number) {
    const last = options.length - 1;
    const next =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? (index + 1) % options.length
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? (index - 1 + options.length) % options.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    const option = next === null ? undefined : options[next];
    if (next === null || option === undefined) return;
    event.preventDefault();
    onChange(option);
    refs.current[next]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="flex max-w-full gap-0.5 overflow-x-auto rounded-xl border border-line bg-surface p-[3px] max-[639px]:grid max-[639px]:w-full max-[639px]:grid-cols-2"
    >
      {options.map((option, index) => {
        const checked = value === option;
        const spans = options.length % 2 === 1 && index === 0;
        return (
          <button
            key={option}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(option)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={`inline-flex min-h-11 flex-none items-center justify-center gap-1.5 rounded-[9px] text-center px-3.5 text-sm leading-tight whitespace-nowrap focus-visible:outline-offset-[-2px] max-[639px]:px-2 max-[639px]:whitespace-normal ${
              spans ? 'max-[639px]:col-span-2' : ''
            } ${
              checked
                ? 'bg-canvas font-semibold text-ink shadow-[inset_0_0_0_1.5px_var(--antoja-ink)]'
                : 'font-medium text-muted hover:text-ink'
            }`}
          >
            {optionLabel(option)}
            {count ? ' ' : null}
            {count ? (
              <span className="font-normal tabular-nums">
                {count(option)}
                {countUnit ? ' ' : null}
                {countUnit ? <span className="sr-only">{countUnit}</span> : null}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
